import { FC, useState } from 'react';
import FullScreenImage from '../fullScreenImage/fullScreenImage';
import Countdown from '../countdown/countdown';
import { useTranslation } from 'react-i18next';
import ModalComponent from '../modal/modal';
import { DetailSection } from '../../pages/harmReports/DetailSection';
import { DetailField } from '../../pages/harmReports/DetailField';
import { SignatureSection } from '../../pages/harmReports/SignatureSection';
import { HarmSignsList } from '../../pages/harmReports/HarmSignsList';
import {
  ReportDetailsProps,
  ReportSection,
  GeneralInfoData,
  PlayerPhotosData,
  PlayerDetailsData,
  HarmSignsData,
  ActionListData,
  NotesData,
  SignatureData,
} from './reportDetails.types';
import { getTranslatedTextValues } from '../../pages/harmReports/harmReportesHelper';
import { useUserDetailContext } from '../../service/context/userDetailContext';
import { UserType } from '@inplay/models';
import { HarmReportsKebabOption } from '../../pages/harmReports/harmReports.type';

export const ReportDetails: FC<ReportDetailsProps> = (props) => {
  const { t } = useTranslation();
  const { config, onClose, onReviewAndSign } = props;
  const { userDetail } = useUserDetailContext();

  const hasReportReviewed = Boolean(config.reviewData?.reviewedBy);
  const isStaff = userDetail?.type === UserType.VENUE_STAFF;
  const hideReviewButton =
    hasReportReviewed || isStaff || Boolean(config.hideReviewAndSign);

  const [modalImage, setModalImage] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const renderSection = (section: ReportSection) => {
    switch (section.type) {
      case 'general-info':
        return renderGeneralInfo(section);
      case 'player-photos':
        return renderPlayerPhotos(section);
      case 'player-details':
        return renderPlayerDetails(section);
      case 'harm-signs':
        return renderHarmSigns(section);
      case 'action-list':
        return renderActionList(section);
      case 'notes':
        return renderNotes(section);
      case 'signature':
        return renderSignature(section);
      default:
        return null;
    }
  };

  const renderGeneralInfo = (section: ReportSection) => {
    const data: GeneralInfoData = section.data;
    const leftFields = data.fields.filter(
      (field) => !field.column || field.column === 1,
    );
    const rightFields = data.fields.filter((field) => field.column === 2);
    console.log('[ReportDetails][renderGeneralInfo]', {
      data,
      leftFields,
      rightFields,
    });

    return (
      <DetailSection title={t(section.titleKey)} columns={2}>
        <div className="flex flex-col space-y-3">
          {leftFields.map((field, index) => (
            <DetailField
              key={index}
              label={t(field.labelKey)}
              value={field.value}
              className={field.className}
            />
          ))}
        </div>
        <div className="flex flex-col space-y-3">
          {rightFields.map((field, index) => (
            <div key={index} className="flex flex-col">
              <span className="text-[#7C7AA8] text-xs">
                {t(field.labelKey)}
              </span>
              {field.isCountdown && field.expiresAt ? (
                <Countdown
                  key={String(field.expiresAt)}
                  expiresAt={field.expiresAt}
                  className="text-red-500 text-xs font-medium"
                />
              ) : field.isStatus ? (
                <div className="flex items-center">
                  <span
                    className={`w-2 h-2 mr-2 rounded-full inline-block ${field.dotColor}`}
                  />
                  <span className="text-[#151740] text-xs font-medium">
                    {field.value}
                  </span>
                </div>
              ) : field.isLinkedReports && field.linkedReports?.length ? (
                <div className="flex flex-wrap gap-1">
                  {field.linkedReports.map((report, idx) => (
                    <span key={report.id}>
                      <span
                        className="text-[#561BDC] text-xs font-medium underline cursor-pointer"
                        onClick={() => props.onLinkedReportClick?.(report.id)}
                      >
                        {report.refNo}
                      </span>
                      {idx < field.linkedReports!.length - 1 && (
                        <span className="text-xs">, </span>
                      )}
                    </span>
                  ))}
                </div>
              ) : (
                <span
                  className={`text-[#151740] text-xs font-medium ${field.className || ''}`}
                >
                  {field.value || '-'}
                </span>
              )}
            </div>
          ))}
        </div>
      </DetailSection>
    );
  };

  const renderPlayerPhotos = (section: ReportSection) => {
    const data = section.data as PlayerPhotosData;
    return (
      <DetailSection title={t(section.titleKey)} columns={2}>
        <img
          className="w-[200px] aspect-square cursor-pointer rounded-2xl hover:opacity-80 transition-opacity"
          src={data.faceImage}
          alt={t('reportDetails.playerFaceAlt')}
          onClick={() => data.faceImage && setModalImage(data.faceImage)}
        />
        <img
          className="w-[130px] h-[200px] cursor-pointer rounded-2xl hover:opacity-80 transition-opacity"
          src={data.bodyImage}
          alt={t('reportDetails.playerBodyAlt')}
          onClick={() => data.bodyImage && setModalImage(data.bodyImage)}
        />
      </DetailSection>
    );
  };

  const renderPlayerDetails = (section: ReportSection) => {
    const data = section.data as PlayerDetailsData;
    return (
      <DetailSection title={t(section.titleKey)} columns={2}>
        <div className="flex flex-col space-y-2">
          {data.leftColumn.map((field, index) => (
            <DetailField
              key={index}
              label={t(field.labelKey)}
              value={field.value}
            />
          ))}
        </div>
        <div className="flex flex-col space-y-2">
          {data.rightColumn.map((field, index) => (
            <DetailField
              key={index}
              label={t(field.labelKey)}
              value={field.value}
            />
          ))}
        </div>
      </DetailSection>
    );
  };

  const renderHarmSigns = (section: ReportSection) => {
    const data: HarmSignsData = section.data;
    return (
      <DetailSection title={t(section.titleKey)} columns={1}>
        <div className="flex flex-col space-y-4">
          <HarmSignsList
            title={t('reportDetails.lengthOfPlay')}
            items={getTranslatedTextValues(data.lengthOfPlay, t)}
          />
          <HarmSignsList
            title={t('reportDetails.money')}
            items={getTranslatedTextValues(data.money, t)}
          />
          <HarmSignsList
            title={t('reportDetails.behaviour')}
            items={getTranslatedTextValues(data.behaviour, t)}
          />
          <HarmSignsList
            title={t('reportDetails.other')}
            items={getTranslatedTextValues(data.other, t)}
          />
        </div>
      </DetailSection>
    );
  };

  const renderActionList = (section: ReportSection) => {
    const data = section.data as ActionListData;
    return (
      <DetailSection title={t(section.titleKey)} columns={1}>
        <HarmSignsList
          title=""
          items={getTranslatedTextValues(data.items, t)}
        />
      </DetailSection>
    );
  };

  const renderNotes = (section: ReportSection) => {
    const data = section.data as NotesData;
    return (
      <DetailSection title={t(section.titleKey)} columns={1}>
        <span className="text-xs">{data.content || '-'}</span>
      </DetailSection>
    );
  };

  const renderSignature = (section: ReportSection) => {
    const data = section.data as SignatureData;

    if (data.isAutoGenerated) {
      return (
        <DetailSection title={t(section.titleKey)} columns={1}>
          <div className="flex flex-col">
            <span className="font-medium text-xs">
              {t('reportDetails.autogeneratedReport')}
            </span>
            <span className="text-gray-600 text-xs">
              {data.autoGeneratedDate
                ? new Date(data.autoGeneratedDate).toLocaleString()
                : '-'}
            </span>
          </div>
        </DetailSection>
      );
    }

    if (!data.signature) return null;

    return (
      <DetailSection title={t(section.titleKey)} columns={1}>
        <SignatureSection
          signature={data.signature.signature}
          firstName={data.signature.firstName}
          lastName={data.signature.lastName}
          date={data.signature.signedDate}
          altText={t('reportDetails.signatureAlt')}
        />
      </DetailSection>
    );
  };

  const renderManagerReview = () => {
    if (!config.reviewData?.reviewedBy) return null;

    return (
      <div className="mx-[-40px] p-9 space-y-8 text-sm sm:text-base bg-[#F9F9FC]">
        <span className="font-semibold mb-2 sm:mb-0 sm:basis-[200px] shrink-0 text-lg">
          {t('reportDetails.managerReview')}
        </span>

        {config.reviewData.staffCompliance !== undefined && (
          <>
            <DetailSection
              title={t('reportDetails.staffCompliance')}
              columns={1}
            >
              <DetailField
                label={t('reportDetails.staffComplianceQuestion')}
                value={
                  config.reviewData.staffCompliance
                    ? t('common.yes')
                    : t('common.no')
                }
              />
            </DetailSection>
            <hr className="border-grey-200" />
          </>
        )}

        {config.reviewData.interventionRequired !== undefined && (
          <>
            <DetailSection
              title={t('harmReports.details.sections.interventionDecision')}
              columns={1}
            >
              <DetailField
                label={t(
                  'harmReports.details.fields.doesPlayerRequireIntervention',
                )}
                value={
                  config.reviewData.interventionRequired
                    ? t('common.yes')
                    : t('common.no')
                }
              />
            </DetailSection>
            <hr className="border-grey-200" />
          </>
        )}

        {config.reviewData.followUpActions && (
          <>
            <DetailSection
              title={t('reportDetails.followUpActions')}
              columns={1}
            >
              <HarmSignsList
                title=""
                items={getTranslatedTextValues(
                  config.reviewData.followUpActions,
                  t,
                )}
              />
            </DetailSection>
            <hr className="border-grey-200" />
          </>
        )}

        <DetailSection title={t('reportDetails.comments')} columns={1}>
          <span className="text-xs">{config.reviewData.comments || '-'}</span>
        </DetailSection>

        <hr className="border-grey-200" />

        <DetailSection title={t('reportDetails.reviewedBy')} columns={1}>
          <SignatureSection
            signature={config.reviewData.reviewedBy.signature}
            firstName={config.reviewData.reviewedBy.firstName}
            lastName={config.reviewData.reviewedBy.lastName}
            date={config.reviewData.reviewedBy.signedDate}
            altText={t('reportDetails.reviewedBySignatureAlt')}
          />
        </DetailSection>
      </div>
    );
  };

  return (
    <ModalComponent
      onToggle={onClose}
      open={true}
      title={`${t(config.titleKey)}${config.reportRefNo ? ` - #${config.reportRefNo}` : ''}`}
      showAction={!!props.kebabOptions?.length}
      menuOpen={menuOpen}
      handleKebabClick={() => setMenuOpen(!menuOpen)}
      closeMenu={() => setMenuOpen(false)}
      kebabOptions={props.kebabOptions}
      kebabOptionUser={{} as any}
      rowIndex={0}
      onKebabSelect={(action) => {
        const shouldClose = props.onShouldCloseKebab?.(action) ?? true;
        if (shouldClose) setMenuOpen(false);
        props.onKebabAction?.(action as HarmReportsKebabOption);
      }}
      isShowClose={true}
      footerHidden
      dialogClassName="w-full max-w-[900px] h-[90vh] overflow-hidden"
      headerClassName="!py-7 !pb-3 h-[80px] items-center !mx-0 px-10"
    >
      <div className="text-sm sm:text-base overflow-y-auto overflow-x-hidden max-h-[calc(90vh-180px)]">
        {config.sections.map((section, index) => (
          <div key={section.id}>
            {renderSection(section)}
            {index < config.sections.length - 1 && (
              <hr className="border-grey-200 my-3" />
            )}
          </div>
        ))}

        {config.hasReviewSection && renderManagerReview()}
      </div>
      {!hideReviewButton && (
        <div
          className="flex items-center justify-end px-10 mx-[-40px] mb-[-8px]"
          style={{
            height: '80px',
            background: '#FFFFFF',
            boxShadow: '0px -2px 16px #4D36CE0F',
            borderRadius: '0px 0px 0px 14px',
          }}
        >
          <button
            className="px-6 py-2 rounded-full bg-[#561BDC] text-white text-sm font-medium"
            onClick={onReviewAndSign}
          >
            {t('reportDetails.reviewAndSign')}
          </button>
        </div>
      )}
      {modalImage && (
        <FullScreenImage src={modalImage} onClose={() => setModalImage(null)} />
      )}
    </ModalComponent>
  );
};

export default ReportDetails;
